import React, {useMemo} from 'react';
import {DailyWorkload, ScheduleCalendarType} from '../../../../../services';
import {CalendarDateHeaderDay} from './CalendarDateHeaderDay';
import {CalendarDateHeaderMonth} from './CalendarDateHeaderMonth';
import {CalendarDateHeaderWeek} from './CalendarDateHeaderWeek';

type Props = {
  isEnabled: boolean;
  date: Date;
  unassignedTasks: number;
  calendarType: ScheduleCalendarType;
  workload: DailyWorkload | undefined;
  onChange: (nextDate: Date, nextCalendarType: ScheduleCalendarType) => void;
};

export const CalendarDateHeader: React.FC<Props> = ({
  date,
  workload,
  isEnabled,
  unassignedTasks,
  calendarType,
  onChange,
}) => {
  // Calculated Workload percentage for day
  const percentage = useMemo(
    () =>
      calendarType !== 'day'
        ? workload!.totalCapacityHours > 0
          ? (100 / workload!.totalCapacityHours) *
            workload!.totalAssignedTaskHours
          : 0
        : 0,
    [calendarType, workload],
  );

  // Calculated Workload percentage kind
  const percentageKind = useMemo(
    () =>
      calendarType !== 'day'
        ? percentage >= 100
          ? 'Negative'
          : percentage < 50
          ? 'Warning'
          : 'Positive'
        : 'Positive',
    [calendarType, percentage],
  );

  return useMemo(() => {
    switch (calendarType) {
      case 'day':
        return <CalendarDateHeaderDay date={date} disabled={!isEnabled} />;
      case 'week':
        return (
          <CalendarDateHeaderWeek
            date={date}
            disabled={!isEnabled}
            percentage={percentage}
            percentageKind={percentageKind}
            showDot={(workload?.totalUnassignedTaskHours ?? 0) > 0}
            onChange={onChange}
            dailyUnassignedTasks={unassignedTasks}
          />
        );
      case 'month':
        return (
          <CalendarDateHeaderMonth
            date={date}
            disabled={!isEnabled}
            percentage={percentage}
            percentageKind={percentageKind}
            showDot={(workload?.totalUnassignedTaskHours ?? 0) > 0}
            onChange={onChange}
            dailyUnassignedTasks={unassignedTasks}
          />
        );
    }
  }, [
    calendarType,
    date,
    isEnabled,
    onChange,
    percentage,
    percentageKind,
    unassignedTasks,
    workload?.totalUnassignedTaskHours,
  ]);
};
